import { Filter } from '@cubejs-client/core';

import { isStringArray } from '@/api/queries/utils';
import { FilterConfigStatic } from '@/types/filter/filterConfig';

import { BRICK_CODE, BRICK_TYPE, GEOGRAPHY_CODE, GEOGRAPHY_TYPE, parseBrickPath } from './salesBrickGeography';

const EMPTY_OPTION_PATHS: readonly string[] = [];

// Brick-level rows (weekly data has no sub-bricks) stay in while the brick itself is checked.
const brickLevelRowsFilter: Filter = { member: GEOGRAPHY_TYPE, operator: 'equals', values: [BRICK_TYPE] };

const buildEqualsFilter = (member: string, values: string[]): Filter => ({ member, operator: 'equals', values });

const groupSubBrickCodesByBrick = (paths: readonly string[]): Map<string, string[]> => {
  const subBrickCodesByBrick = new Map<string, string[]>();
  paths.forEach(path => {
    const brickPath = parseBrickPath(path);
    if (!brickPath?.subBrickCode) return;
    const subBrickCodes = subBrickCodesByBrick.get(brickPath.brickCode);
    if (subBrickCodes) subBrickCodes.push(brickPath.subBrickCode);
    else subBrickCodesByBrick.set(brickPath.brickCode, [brickPath.subBrickCode]);
  });
  return subBrickCodesByBrick;
};

// The option universe holds up to 50K paths and applyFilter runs for every global filter consumer.
const knownSubBrickCodesCache = new WeakMap<readonly string[], ReadonlyMap<string, string[]>>();

const getKnownSubBrickCodesByBrick = (optionPaths: readonly string[]): ReadonlyMap<string, string[]> => {
  const cachedGrouping = knownSubBrickCodesCache.get(optionPaths);
  if (cachedGrouping) return cachedGrouping;

  const grouping = groupSubBrickCodesByBrick(optionPaths);
  knownSubBrickCodesCache.set(optionPaths, grouping);
  return grouping;
};

const isWholeBrickSelected = (selectedSubBrickCodes: readonly string[], knownSubBrickCodes: readonly string[]) => {
  if (!selectedSubBrickCodes.length) return true;
  const selectedCodes = new Set(selectedSubBrickCodes);
  return knownSubBrickCodes.length > 0 && knownSubBrickCodes.every(subBrickCode => selectedCodes.has(subBrickCode));
};

/**
 * Filters the checked bricks and sub-bricks. A brick whose known sub-bricks are all checked (or none) is
 * matched whole; without the option universe, the checked sub-bricks are listed explicitly.
 */
export const buildSalesBrickFilter = (
  value: unknown,
  optionPaths: readonly string[] = EMPTY_OPTION_PATHS,
): Filter | undefined => {
  if (!isStringArray(value) || !value.length) return undefined;

  const knownSubBrickCodesByBrick = getKnownSubBrickCodesByBrick(optionPaths);
  const selectedSubBrickCodesByBrick = groupSubBrickCodesByBrick(value);
  const selectedBrickCodes = new Set<string>();
  value.forEach(path => {
    const brickPath = parseBrickPath(path);
    if (brickPath && !brickPath.subBrickCode) selectedBrickCodes.add(brickPath.brickCode);
  });

  const wholeBrickCodes: string[] = [];
  const brickScopeFilters: Filter[] = [];

  selectedBrickCodes.forEach(brickCode => {
    const selectedSubBrickCodes = selectedSubBrickCodesByBrick.get(brickCode) ?? [];
    if (isWholeBrickSelected(selectedSubBrickCodes, knownSubBrickCodesByBrick.get(brickCode) ?? [])) {
      wholeBrickCodes.push(brickCode);
      return;
    }
    brickScopeFilters.push({
      and: [
        buildEqualsFilter(BRICK_CODE, [brickCode]),
        { or: [buildEqualsFilter(GEOGRAPHY_CODE, selectedSubBrickCodes), brickLevelRowsFilter] },
      ],
    });
  });

  selectedSubBrickCodesByBrick.forEach((subBrickCodes, brickCode) => {
    if (selectedBrickCodes.has(brickCode)) return;
    brickScopeFilters.push({
      and: [buildEqualsFilter(BRICK_CODE, [brickCode]), buildEqualsFilter(GEOGRAPHY_CODE, subBrickCodes)],
    });
  });

  const brickFilters = wholeBrickCodes.length
    ? [buildEqualsFilter(BRICK_CODE, wholeBrickCodes), ...brickScopeFilters]
    : brickScopeFilters;

  return brickFilters.length > 1 ? { or: brickFilters } : brickFilters[0];
};

const salesBrickFilter: FilterConfigStatic<string> = {
  label: 'global.filters.formControls.salesBrickSubBrickLabel',
  placeholder: 'global.filters.formControls.salesBrickSubBrickLabel',
  // SalesBrickFilter loads the options, as their codes must be shown by name.
  options: [],
  defaultValue: [],
  type: 'treeselect',
  selectionMode: 'indication',
  searchable: true,
  clearable: true,
  applyFilter: (value, context) => buildSalesBrickFilter(value, context?.optionPaths),
};

export default salesBrickFilter;
